import type { ECharts } from "echarts/core";

export interface CrosshairBus {
    register: (chart: ECharts) => void;
    unregister: (chart: ECharts) => void;
}

type ZrPointerEvent = { offsetX: number; offsetY: number };

type MouseHandlers = {
    onMouseMove: (event: ZrPointerEvent) => void;
    onGlobalOut: () => void;
};

export function createCrosshairBus(): CrosshairBus {
    let broadcasting = false;
    const charts = new Set<ECharts>();
    const mouseHandlers = new Map<ECharts, MouseHandlers>();

    return {
        register: (sourceChart) => {
            const existingHandlers = mouseHandlers.get(sourceChart);
            if (existingHandlers) {
                const zr = sourceChart.getZr();
                zr.off("mousemove", existingHandlers.onMouseMove);
                zr.off("globalout", existingHandlers.onGlobalOut);
            }

            charts.add(sourceChart);

            const onMouseMove = (event: ZrPointerEvent) => {
                if (broadcasting) return;
                broadcasting = true;
                charts.forEach((peerChart) => {
                    if (peerChart === sourceChart) return;
                    const xFrom = sourceChart.convertFromPixel({ xAxisIndex: 0 }, event.offsetX);
                    const xTo = peerChart.convertToPixel({ xAxisIndex: 0 }, xFrom);

                    // Not showTip: the peer would get its own box repeating its own rows.
                    // y only has to land inside the peer's grid — the crosshair is vertical.
                    peerChart.dispatchAction({
                        type: "updateAxisPointer",
                        x: xTo,
                        y: peerChart.getHeight() / 2,
                        tooltipOption: { showContent: false },
                    });
                });
                broadcasting = false;
            };

            // Nothing in echarts' axisPointer listens for hideTip, so a dispatched peer keeps its line.
            const onGlobalOut = () => {
                broadcasting = true;
                charts.forEach((chart) => {
                    chart.dispatchAction({ type: "updateAxisPointer", currTrigger: "leave" });
                });
                broadcasting = false;
            };

            mouseHandlers.set(sourceChart, { onMouseMove, onGlobalOut });
            const zr = sourceChart.getZr();
            zr.on("mousemove", onMouseMove);
            zr.on("globalout", onGlobalOut);
        },
        unregister: (sourceChart) => {
            const handlers = mouseHandlers.get(sourceChart);
            if (handlers) {
                const zr = sourceChart.getZr();
                zr.off("mousemove", handlers.onMouseMove);
                zr.off("globalout", handlers.onGlobalOut);
                mouseHandlers.delete(sourceChart);
            }
            charts.delete(sourceChart);
        },
    };
}
